import React from "react";
import { CalendarDays } from "lucide-react";

const HistoryTab = ({ history = [], employee }) => {
  const fullName = employee
    ? employee.full_name || `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
    : "Employee";

  // Synthesize history to ensure it includes the "Joined" milestone at the beginning
  const sortedHistory = React.useMemo(() => {
    if (!employee) return [];

    const list = [...history];

    // Check if a "Joined" event already exists
    const hasJoinedEvent = list.some(
      (item) => item.change_type?.toLowerCase() === "joined"
    );

    if (!hasJoinedEvent) {
      // Prepend the Joined event
      list.unshift({
        uuid: `join_${employee.uuid}`,
        change_type: "Joined",
        reason: "Employee joined the organization.",
        effective_date: employee.joining_date,
        change_metadata: {
          employment_type: employee.employment_type_name,
          department: employee.department_name,
        },
      });
    }

    // Sort by date descending to show timeline from newest to oldest
    return list.sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
  }, [history, employee]);

  return (
    <div className="p-6 space-y-6">
      {sortedHistory.length > 0 ? (
        <div className="relative border-l-2 border-slate-100 ml-3.5 pl-6 space-y-6">
          {sortedHistory.map((item) => (
            <div key={item.uuid} className="relative">
              {/* Timeline marker */}
              <span className="absolute -left-[31px] top-1.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-white border-2 border-[#147db5]" />

              <div className="flex flex-col gap-1">
                {/* Header: Title & Date */}
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-800">
                    {item.change_type || "Employment Change"}
                  </h3>
                  <span className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                    <CalendarDays size={13} />
                    {formatDate(item.effective_date)}
                  </span>
                </div>

                {/* Subtitle / Reason */}
                <p className="text-xs text-slate-500">
                  {item.reason}
                </p>

                {/* Metadata Card */}
                <div className="mt-2.5 rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-xs space-y-1.5">
                  <div className="flex justify-between items-center text-slate-600">
                    <span className="font-semibold text-slate-800">{fullName}</span>
                    <span className="text-[10px] text-slate-400 font-semibold bg-white border border-slate-100 rounded px-1.5 py-0.5">
                      {employee.employee_id || "EMP-XXXX"}
                    </span>
                  </div>

                  {item.change_metadata &&
                    Object.entries(item.change_metadata).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-slate-500">
                        <span className="font-medium capitalize">{formatLabel(key)}</span>
                        <span className="font-semibold text-slate-700">{String(value ?? "—")}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-sm text-slate-400">No employment history found.</p>
        </div>
      )}
    </div>
  );
};

const formatDate = (date) => {
  if (!date) return "—";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return date;
  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") => {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export default HistoryTab;